import type {
  CdcBboxParams,
  CdcHealthResponse,
  CdcOwnerInfo,
  CdcRelatedVesselsResponse,
  CdcSearchResponse,
  CdcVessel,
  CdcVesselHistory,
} from '../types';

const CDC_API_URL = import.meta.env.VITE_CDC_API_URL || 'http://localhost:8090';

/**
 * Generic fetch function for CDC API (no auth required - PoC mode)
 */
export async function cdcFetch<T>(endpoint: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${CDC_API_URL}${endpoint}`, signal ? { signal } : undefined);
  if (!res.ok) {
    throw new Error(`CDC API error: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

/**
 * Check CDC API health status
 */
export async function checkCdcHealth(): Promise<CdcHealthResponse> {
  return cdcFetch<CdcHealthResponse>('/health');
}

/**
 * Fetch vessels within a bounding box (full data - 14 fields)
 * Use fetchCdcVesselsBboxSlim for clustering/map display
 */
export async function fetchCdcVesselsBbox(bbox: CdcBboxParams): Promise<GeoJSON.FeatureCollection> {
  const params = new URLSearchParams({
    minLat: bbox.minLat.toString(),
    maxLat: bbox.maxLat.toString(),
    minLon: bbox.minLon.toString(),
    maxLon: bbox.maxLon.toString(),
    limit: (bbox.limit || 10000).toString(),
  });
  if (bbox.max_age_hours) {
    params.set('max_age_hours', bbox.max_age_hours.toString());
  }
  return cdcFetch<GeoJSON.FeatureCollection>(`/vessels/bbox?${params}`);
}

/**
 * Fetch vessels within a bounding box (slim data for clustering)
 * Returns only: mmsi, lat, lon, ship_type, cog
 * ~80% smaller payload than full endpoint
 */
export async function fetchCdcVesselsBboxSlim(bbox: CdcBboxParams): Promise<GeoJSON.FeatureCollection> {
  const params = new URLSearchParams({
    minLat: bbox.minLat.toString(),
    maxLat: bbox.maxLat.toString(),
    minLon: bbox.minLon.toString(),
    maxLon: bbox.maxLon.toString(),
    limit: (bbox.limit || 10000).toString(),
  });
  if (bbox.max_age_hours) {
    params.set('max_age_hours', bbox.max_age_hours.toString());
  }
  return cdcFetch<GeoJSON.FeatureCollection>(`/vessels/bbox/slim?${params}`);
}

/**
 * Fetch a single vessel by MMSI
 */
export async function fetchCdcVessel(mmsi: string): Promise<{ found: boolean; vessel?: CdcVessel }> {
  return cdcFetch<{ found: boolean; vessel?: CdcVessel }>(`/vessels/${mmsi}`);
}

/**
 * Fetch vessel history for animation playback
 */
export async function fetchCdcVesselHistory(mmsi: string, hours = 6): Promise<CdcVesselHistory> {
  // Backend caps at 10000 points. Use max for longer ranges to avoid truncation.
  const limit = hours > 24 ? 10000 : 1000;
  return cdcFetch<CdcVesselHistory>(`/vessels/${mmsi}/history?hours=${hours}&limit=${limit}`);
}

/**
 * Fetch vessel owner information
 */
export async function fetchCdcVesselOwner(mmsi: string): Promise<CdcOwnerInfo> {
  return cdcFetch<CdcOwnerInfo>(`/vessels/${mmsi}/owner`);
}

/**
 * Fetch related vessels (nearby, same type, same destination, same flag, same owner)
 * Supports combining multiple filters with AND logic
 *
 * API Parameters:
 * - radius_m: Distance in meters for nearby filter (e.g., 30km = 30000)
 * - same_type: true/false
 * - same_destination: true/false
 * - same_flag: true/false
 * - same_owner: true/false
 * - order_by: 'recency' | 'distance'
 * - limit: max results
 */
export async function fetchCdcRelatedVessels(
  mmsi: string,
  options: {
    radius_m?: number;
    same_type?: boolean;
    same_destination?: boolean;
    same_flag?: boolean;
    same_owner?: boolean;
    order_by?: 'recency' | 'distance';
    limit?: number;
  } = {},
): Promise<CdcRelatedVesselsResponse> {
  const params = new URLSearchParams();
  if (options.radius_m) params.set('radius_m', options.radius_m.toString());
  if (options.same_type) params.set('same_type', 'true');
  if (options.same_destination) params.set('same_destination', 'true');
  if (options.same_flag) params.set('same_flag', 'true');
  if (options.same_owner) params.set('same_owner', 'true');
  if (options.order_by) params.set('order_by', options.order_by);
  if (options.limit) params.set('limit', options.limit.toString());

  return cdcFetch<CdcRelatedVesselsResponse>(`/vessels/${mmsi}/related?${params}`);
}

/**
 * Search vessels by name, MMSI, or IMO
 */
export async function searchCdcVessels(query: string, limit = 10): Promise<CdcSearchResponse> {
  const params = new URLSearchParams({
    q: query,
    limit: limit.toString(),
  });
  return cdcFetch<CdcSearchResponse>(`/vessels/search?${params}`);
}

/**
 * Fetch ALL vessels globally for client-side clustering (SLIM data)
 * Returns only: mmsi, lat, lon, ship_type, cog — 80% smaller payload
 * Used for Cassandra-style approach: load once at startup, Mapbox GPU clusters
 *
 * Trade-off: ~10-20s startup, but INSTANT zoom/pan after that
 */
export async function fetchCdcVesselsGlobalSlim(limit = 100000): Promise<GeoJSON.FeatureCollection> {
  const params = new URLSearchParams({
    minLat: '-90',
    maxLat: '90',
    minLon: '-180',
    maxLon: '180',
    limit: limit.toString(),
  });
  return cdcFetch<GeoJSON.FeatureCollection>(`/vessels/bbox/slim?${params}`);
}

/**
 * Fetch clustered vessels for global display (server-side clustering)
 * At low zoom (0-7): Returns pre-aggregated clusters with counts
 * At high zoom (8+): Returns individual vessels
 *
 * Use this for global map display instead of bbox/slim to get full coverage
 */
export async function fetchCdcVesselsClustered(
  zoom: number,
  bbox?: { minLat: number; maxLat: number; minLon: number; maxLon: number },
  limit = 10000,
): Promise<GeoJSON.FeatureCollection> {
  const params = new URLSearchParams({
    zoom: Math.floor(zoom).toString(),
    limit: limit.toString(),
  });

  // Use world-wide bbox if not specified (for global coverage)
  if (bbox) {
    params.set('minLat', bbox.minLat.toString());
    params.set('maxLat', bbox.maxLat.toString());
    params.set('minLon', bbox.minLon.toString());
    params.set('maxLon', bbox.maxLon.toString());
  } else {
    params.set('minLat', '-90');
    params.set('maxLat', '90');
    params.set('minLon', '-180');
    params.set('maxLon', '180');
  }

  return cdcFetch<GeoJSON.FeatureCollection>(`/vessels/bbox/clustered?${params}`);
}

/**
 * Dark vessels response type
 */
export interface CdcDarkVesselsResponse extends GeoJSON.FeatureCollection {
  metadata: {
    threshold_hours: number;
    count: number;
    cutoff_time: string;
  };
}

/**
 * Fetch dark vessels (vessels that haven't reported in X hours)
 * Returns GeoJSON with metadata including count
 */
export async function fetchCdcDarkVessels(
  hours = 72,
  limit = 1000,
  options?: {
    ship_type?: string;
    flag_country?: string;
  },
): Promise<CdcDarkVesselsResponse> {
  const params = new URLSearchParams({
    hours: hours.toString(),
    limit: limit.toString(),
  });
  if (options?.ship_type) params.set('ship_type', options.ship_type);
  if (options?.flag_country) params.set('flag_country', options.flag_country);

  return cdcFetch<CdcDarkVesselsResponse>(`/vessels/dark?${params}`);
}

// ─── Area Traffic History ─────────────────────────────────────────

export interface CdcAreaHistoryResponse {
  vessels: Array<{
    mmsi: string;
    name?: string;
    imo?: string;
    ship_type?: number;
    flag_country?: string;
    destination?: string;
    first_ts: string;
    last_ts: string;
    status: 'inside' | 'entered' | 'exited' | 'passed_through';
    avg_sog?: number;
    min_sog?: number;
    max_sog?: number;
    entry_point?: { lat: number; lon: number; ts: string } | null;
    exit_point?: { lat: number; lon: number; ts: string } | null;
    positions: Array<{
      ts: string;
      lat: number;
      lon: number;
      sog?: number;
      cog?: number;
      heading?: number | null;
    }>;
  }>;
  metadata: {
    stats: {
      vessel_count: number;
      total_positions: number;
      timeline_bucket_hours?: number;
    };
    time_range: {
      start: string;
      end: string;
    };
    bbox: {
      minLat: number;
      maxLat: number;
      minLon: number;
      maxLon: number;
    };
    timeline?: Array<{ ts: string; count: number }>;
    downsampling?: string;
  };
}

function parseJsonProp<T>(value: unknown): T | null {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  }
  return value as T;
}

function transformGeoJSONResponse(geojson: GeoJSON.FeatureCollection): CdcAreaHistoryResponse {
  const vessels = geojson.features.map((feature) => {
    const props = feature.properties || {};

    let positions: CdcAreaHistoryResponse['vessels'][0]['positions'];
    const rawPositions = parseJsonProp<Array<Record<string, unknown>>>(props.positions);

    if (Array.isArray(rawPositions) && rawPositions.length > 0) {
      positions = rawPositions.map(
        (p: { ts: string; lat: number; lon: number; sog?: number; cog?: number; heading?: number | null }) => ({
          ts: p.ts,
          lat: p.lat,
          lon: p.lon,
          sog: p.sog ?? 0,
          cog: p.cog ?? 0,
          heading: p.heading ?? null,
        }),
      );
    } else {
      const geom = feature.geometry as GeoJSON.Point;
      const [lon, lat] = geom.coordinates;
      positions = [
        {
          ts: props.last_ts || props.first_ts || new Date().toISOString(),
          lat,
          lon,
          sog: props.avg_sog || 0,
          cog: 0,
          heading: null,
        },
      ];
    }

    return {
      mmsi: props.mmsi,
      name: props.name,
      imo: props.imo,
      ship_type: props.ship_type,
      flag_country: props.flag_country,
      destination: props.destination,
      first_ts: props.first_ts,
      last_ts: props.last_ts,
      status: props.status,
      avg_sog: props.avg_sog,
      min_sog: props.min_sog,
      max_sog: props.max_sog,
      entry_point: parseJsonProp<{ lat: number; lon: number; ts: string }>(props.entry_point),
      exit_point: parseJsonProp<{ lat: number; lon: number; ts: string }>(props.exit_point),
      positions,
    };
  });

  return {
    vessels,
    metadata: (geojson as unknown as { metadata: CdcAreaHistoryResponse['metadata'] }).metadata,
  };
}

export async function fetchCdcAreaHistory(
  bbox: {
    minLat: number;
    maxLat: number;
    minLon: number;
    maxLon: number;
  },
  days = 7,
  limit = 100,
): Promise<CdcAreaHistoryResponse> {
  const params = new URLSearchParams({
    minLat: bbox.minLat.toString(),
    maxLat: bbox.maxLat.toString(),
    minLon: bbox.minLon.toString(),
    maxLon: bbox.maxLon.toString(),
    days: days.toString(),
    limit: limit.toString(),
  });

  const geojson = await cdcFetch<GeoJSON.FeatureCollection>(`/vessels/area-history?${params}`);
  return transformGeoJSONResponse(geojson);
}

// ─── Time Machine ─────────────────────────────────────────────────

export interface CdcDensityHistoryResponse {
  cells: Array<{
    lat: number;
    lon: number;
    buckets: Array<{ ts: string; count: number }>;
  }>;
  metadata: {
    time_range: { start: string; end: string };
    total_positions: number;
    cell_count: number;
    grid_resolution: number;
    bucket_hours: number;
  };
}

export async function fetchTimeMachineVessels(
  bbox: { minLat: number; maxLat: number; minLon: number; maxLon: number },
  days: number,
  zoom: number,
  signal?: AbortSignal,
): Promise<CdcAreaHistoryResponse> {
  const params = new URLSearchParams({
    minLat: bbox.minLat.toString(),
    maxLat: bbox.maxLat.toString(),
    minLon: bbox.minLon.toString(),
    maxLon: bbox.maxLon.toString(),
    days: days.toString(),
    zoom: Math.floor(zoom).toString(),
  });

  const geojson = await cdcFetch<GeoJSON.FeatureCollection>(`/vessels/time-machine?${params}`, signal);
  return transformGeoJSONResponse(geojson);
}

export async function fetchTimeMachineDensity(
  bbox: { minLat: number; maxLat: number; minLon: number; maxLon: number },
  days: number,
  zoom: number,
  signal?: AbortSignal,
): Promise<CdcDensityHistoryResponse> {
  const params = new URLSearchParams({
    minLat: bbox.minLat.toString(),
    maxLat: bbox.maxLat.toString(),
    minLon: bbox.minLon.toString(),
    maxLon: bbox.maxLon.toString(),
    days: days.toString(),
    zoom: Math.floor(zoom).toString(),
  });

  return cdcFetch<CdcDensityHistoryResponse>(`/vessels/density-history?${params}`, signal);
}
